import React, { useState, useMemo, useEffect } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";
import { MdSearch, MdFileDownload, MdPictureAsPdf, MdRefresh } from "react-icons/md";
import * as XLSX from "xlsx";

const OutletOrderReport = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [search, setSearch] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  
  // Party Selection State
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");

  // Fetch Active Parties ONLY for the selected date automatically upon change
  useEffect(() => {
    const fetchActiveParties = async () => {
      if (!selectedDate) return;
      try {
        // Query root `orders` instead of `outletOrders` or `partyMaster`. 
        // 50 invoices = 50 reads. Exceedingly cheap and precise.
        const q = query(
          collection(db, "orders"),
          where("approvedDateOnly", "==", selectedDate),
          where("status", "==", "APPROVED")
        );
        const snap = await getDocs(q);
        
        const uniqueParties = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.partyId && data.partyName) {
            uniqueParties[data.partyId] = data.partyName;
          }
        });

        const partyArray = Object.entries(uniqueParties).map(([id, name]) => ({
          partyId: id,
          name: name
        }));
        
        // Sort alphabetically
        partyArray.sort((a, b) => a.name.localeCompare(b.name));
        
        setParties(partyArray);
        setSelectedParty(""); // Reset selection when date changes
      } catch (err) {
        console.error("Error fetching active parties:", err);
      }
    };
    
    fetchActiveParties();
  }, [selectedDate]);

  const fetchOrders = async () => {
    if (!selectedDate) {
      alert("Please select a specific Invoice Date.");
      return;
    }
    if (!selectedParty) {
      alert("Please select a Party from the dropdown (or select 'ALL Parties' at the bottom).");
      return;
    }

    setLoading(true);
    setOrders([]); // clear previous
    try {
      let q;
      
      // OPTIMIZATON: Force reading strictly the selected date to destroy bill costs
      if (selectedParty === "ALL") {
          q = query(
            collection(db, "outletOrders"),
            where("approvedDateOnly", "==", selectedDate)
          );
      } else {
          q = query(
            collection(db, "outletOrders"),
            where("approvedDateOnly", "==", selectedDate),
            where("partyId", "==", selectedParty)
          );
      }

      const snap = await getDocs(q);
      
      // Sort dynamically locally to avoid complex composite index errors
      const docsData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docsData.sort((a, b) => (b.approvedDateOnly || "").localeCompare(a.approvedDateOnly || ""));
      
      setOrders(docsData);
      setHasFetched(true);
    } catch (err) {
      console.error("Error fetching outlet orders:", err);
      // If composite index is missing, firebase gives a direct link in the error
      alert("Error fetching data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const term = search.toLowerCase();
      
      const matchesSearch = !term ||
        o.displayOrderId?.toLowerCase().includes(term) ||
        o.outletName?.toLowerCase().includes(term) ||
        o.partyName?.toLowerCase().includes(term) ||
        o.vehicleNo?.toLowerCase().includes(term);
        
      return matchesSearch;
    });
  }, [orders, search]);

  const exportToExcel = () => {
    if (filteredOrders.length === 0) {
      alert("No data to export");
      return;
    }

    const rows = [];

    filteredOrders.forEach(o => {
      // 1. NEW FLAT STRUCTURE: If the document itself represents a single SKU
      if (o.sku && o.qty !== undefined) {
        rows.push({
          "Date": o.approvedDateOnly || "",
          "Order ID": o.displayOrderId || "",
          "Outlet Name": o.outletName || "",
          "Party Name": o.partyName || "",
          "SKU": o.sku || "",
          "Item Name": o.skuName || o.description || "",
          "Qty": Number(o.qty || 0),
          "Basic Rate": Number(o.rate || 0),
          "Basic Amount": Number(o.basicAmount || 0),
          "GST %": Number(o.gstPercent || 0),
          "CGST Amount": Number(o.cgstAmount || (o.gstAmount / 2) || 0),
          "SGST Amount": Number(o.sgstAmount || (o.gstAmount / 2) || 0),
          "Total GST": Number(o.gstAmount || 0),
          "Cess Amount": Number(o.cessAmount || 0),
          "Addl Cess": Number(o.addlCessAmount || 0),
          "Total Amount": Number((o.totalAmount || o.ewayAmount || 0).toFixed(4)),
          "Vehicle No": o.vehicleNo || "",
          "Route": o.route || "",
          "GSTIN": o.gstno || o.gstin || "",
          "DMS Name": o.dmsName || "",
          "EasyCig Name": o.easyCigName || "",
          "Root Order ID": o.rootOrderId || "",
          "Status": o.status || "UNKNOWN",
        });
      }
      // 2. NEW EMBEDDED STRUCTURE: If order has embedded lines array
      else if (Array.isArray(o.lines) && o.lines.length > 0) {
        o.lines.forEach(line => {
          rows.push({
            "Date": o.approvedDateOnly || "",
            "Order ID": o.displayOrderId || "",
            "Outlet Name": o.outletName || "",
            "Party Name": o.partyName || "",
            "SKU": line.sku || "",
            "Item Name": line.skuName || line.description || "",
            "Qty": Number(line.qty || 0),
            "Basic Rate": Number(line.rate || line.basicRate || 0),
            "Basic Amount": Number(line.basicAmount || 0),
            "GST %": Number(line.gstPercent || 0),
            "CGST Amount": Number((line.gstAmount / 2).toFixed(4)),
            "SGST Amount": Number((line.gstAmount / 2).toFixed(4)),
            "Total GST": Number(line.gstAmount || 0),
            "Cess Amount": Number(line.cessAmount || 0),
            "Addl Cess": Number(line.addlCessAmount || 0),
            "Total Amount": Number((line.totalAmount || line.ewayLineTotal || line.lineTotal || 0).toFixed(4)),
            "Vehicle No": o.vehicleNo || "",
            "Route": o.route || "",
            "GSTIN": o.gstno || o.gstin || "",
            "DMS Name": o.dmsName || "",
            "EasyCig Name": o.easyCigName || "",
            "Root Order ID": o.rootOrderId || "",
            "Status": o.status || "UNKNOWN",
          });
        });
      }
      // 3. OLD STRUCTURE: If order has orderLines (legacy wrapper)
      else if (Array.isArray(o.orderLines) && o.orderLines.length > 0) {
        o.orderLines.forEach(line => {
          rows.push({
            "Date": o.approvedDateOnly || "",
            "Order ID": o.displayOrderId || "",
            "Outlet Name": o.outletName || "",
            "Party Name": o.partyName || "",
            "SKU": line.sku || "",
            "Item Name": line.skuName || line.description || "",
            "Qty": Number(line.qty || 0),
            "Basic Rate": Number(line.rate || 0),
            "Basic Amount": Number(line.basicAmount || 0),
            "GST %": Number(line.gstPercent || 0),
            "CGST Amount": Number((line.gstAmount / 2).toFixed(4)),
            "SGST Amount": Number((line.gstAmount / 2).toFixed(4)),
            "Total GST": Number(line.gstAmount || 0),
            "Cess Amount": Number(line.cessAmount || 0),
            "Addl Cess": Number(line.addlCessAmount || 0),
            "Total Amount": Number((line.totalAmount || line.ewayAmount || 0).toFixed(4)),
            "Vehicle No": o.vehicleNo || "",
            "Route": o.route || "",
            "GSTIN": o.gstno || o.gstin || "",
            "DMS Name": o.dmsName || "",
            "EasyCig Name": o.easyCigName || "",
            "Root Order ID": o.rootOrderId || "",
            "Status": o.status || "UNKNOWN",
          });
        });
      } 
      // 4. Fallback for orders without any item data
      else {
        rows.push({
          "Date": o.approvedDateOnly || "",
          "Order ID": o.displayOrderId || "",
          "Outlet Name": o.outletName || "",
          "Party Name": o.partyName || "",
          "SKU": "SUMMARY",
          "Item Name": "Order Summary",
          "Qty": o.totalQty || 0,
          "Total Amount": o.totalAmount || 0,
          "Vehicle No": o.vehicleNo || "",
          "GSTIN": o.gstno || o.gstin || "",
          "Root Order ID": o.rootOrderId || "",
          "Status": o.status || "UNKNOWN",
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GST_Filing_Data");
    XLSX.writeFile(wb, `Outlet_Orders_GST_Ready_${selectedDate}.xlsx`);
  };

  return (
    <Layout>
      <div style={{ padding: "24px", background: "#f8f9fb", minHeight: "100vh" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#1a202c" }}>Outlet Orders Report</h2>
            <p style={{ margin: "4px 0 0 0", color: "#718096", fontSize: "14px" }}>Strict single-day query matrix for read optimization</p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button onClick={exportToExcel} disabled={filteredOrders.length === 0} style={{ 
              display: "flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "#198754", 
              color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer",
              opacity: filteredOrders.length === 0 ? 0.5 : 1
            }}>
              <MdFileDownload size={20} /> Export Detailed Excel
            </button>
          </div>
        </div>

        {/* --- HIGH PERFORMANCE CONTROL BAR --- */}
        <div style={{ background: "#fff", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "24px", display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-end" }}>
          
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#4a5568", marginBottom: "8px", textTransform: "uppercase" }}>Target Date</label>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e0" }} />
          </div>

          <div style={{ flex: 2, minWidth: "250px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#4a5568", marginBottom: "8px", textTransform: "uppercase" }}>Select Party ({parties.length} Active Today)</label>
            <select 
              value={selectedParty} 
              onChange={e => setSelectedParty(e.target.value)}
              style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e0", background: "#fff" }}
              disabled={parties.length === 0}
            >
              <option value="" disabled>
                {parties.length === 0 ? `-- No Parties Billed on ${selectedDate} --` : `-- Select an Active Party --`}
              </option>
              {parties.map(p => (
                <option key={p.partyId} value={p.partyId}>
                  {p.partyId === p.name ? p.name : `${p.partyId} - ${p.name}`}
                </option>
              ))}
              {parties.length > 0 && <option disabled>───────────</option>}
              {parties.length > 0 && <option value="ALL" style={{ fontWeight: "bold", color: "#d32f2f" }}>⚠️ Select ALL {parties.length} Parties (High Cost Warning)</option>}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: "150px" }}>
            <button onClick={fetchOrders} disabled={loading} style={{ 
                width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "11px 20px", background: "#3182ce", 
                color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, cursor: "pointer",
                opacity: loading ? 0.7 : 1
              }}>
                <MdRefresh size={20} className={loading ? "animate-spin" : ""} /> {loading ? "Fetching..." : "Fetch Data"}
            </button>
          </div>

          <div style={{ flex: 2, minWidth: "250px", position: "relative" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#4a5568", marginBottom: "8px", textTransform: "uppercase" }}>Local Keyword Search</label>
            <input type="text" placeholder="Search ID, Outlet, Vehicle..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: "100%", padding: "10px 10px 10px 40px", borderRadius: "8px", border: "1px solid #cbd5e0" }} />
            <MdSearch size={20} style={{ position: "absolute", left: "12px", bottom: "12px", color: "#a0aec0" }} />
          </div>

        </div>

        {/* TABLE CONTAINER WITH HORIZONTAL SCROLL */}
        <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflowX: "auto" }}>
          <table style={{ minWidth: "2000px", width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Outlet Name</th>
                <th style={thStyle}>Party Name</th>
                <th style={thStyle}>SKU</th>
                <th style={thStyle}>Item Name</th>
                <th style={thStyle}>Qty</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Vehicle No</th>
                <th style={thStyle}>Route</th>
                <th style={thStyle}>Salesman</th>
                <th style={thStyle}>Party Phone</th>
                <th style={thStyle}>Party Type</th>
                <th style={thStyle}>DMS Name</th>
                <th style={thStyle}>EasyCig Name</th>
                <th style={thStyle}>GST No</th>
                <th style={thStyle}>Address</th>
                <th style={thStyle}>Root Order</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>PDF Links</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="20" style={{ padding: "40px", textAlign: "center", color: "#718096" }}>Targeting secured data matrix...</td></tr>
              ) : !hasFetched ? (
                <tr><td colSpan="20" style={{ padding: "40px", textAlign: "center", color: "#718096" }}>Select a specific Date and Party to responsibly fetch records.</td></tr>
              ) : filteredOrders.length === 0 ? (
                <tr><td colSpan="20" style={{ padding: "40px", textAlign: "center", color: "#718096" }}>No orders found for this exact Date/Party combination.</td></tr>
              ) : filteredOrders.map(o => (
                <tr key={o.id} style={{ borderBottom: "1px solid #edf2f7", fontSize: "13px" }}>
                  <td style={tdStyle}>{o.approvedDateOnly}</td>
                  <td style={{ ...tdStyle, fontWeight: 700, color: "#2d3748" }}>{o.displayOrderId}</td>
                  <td style={tdStyle}>{o.outletName}</td>
                  <td style={tdStyle}>{o.partyName}</td>
                  <td style={tdStyle}>{o.sku || "OLD SUMMARY"}</td>
                  <td style={{ ...tdStyle, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>{o.skuName || o.description || ""}</td>
                  <td style={tdStyle}>{Number(o.qty || o.totalQty || 0).toFixed(2)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>₹{Number(o.totalAmount || 0).toLocaleString()}</td>
                  <td style={tdStyle}>{o.vehicleNo}</td>
                  <td style={tdStyle}>{o.route}</td>
                  <td style={tdStyle}>{o.salesman}</td>
                  <td style={tdStyle}>{o.partyPhone}</td>
                  <td style={tdStyle}>{o.partyType}</td>
                  <td style={tdStyle}>{o.dmsName}</td>
                  <td style={tdStyle}>{o.easyCigName}</td>
                  <td style={tdStyle}>{o.gstno || o.gstin}</td>
                  <td style={{ ...tdStyle, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>{o.address}</td>
                  <td style={tdStyle}>{o.rootOrderId}</td>
                  <td style={tdStyle}>
                    <span style={{ 
                      padding: "4px 8px", 
                      background: o.status === "CANCELLED" ? "#fee2e2" : "#d1fae5", 
                      color: o.status === "CANCELLED" ? "#991b1b" : "#065f46", 
                      borderRadius: "10px", 
                      fontSize: "10px", 
                      fontWeight: 700 
                    }}>
                      {o.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: "10px" }}>
                      {o.challanPdfUrl && (
                        <a href={o.challanPdfUrl} target="_blank" rel="noreferrer" title="Challan" style={{ color: "#e53e3e" }}><MdPictureAsPdf size={18} /></a>
                      )}
                      {o.tripSheetPdfUrl && (
                        <a href={o.tripSheetPdfUrl} target="_blank" rel="noreferrer" title="Trip Sheet" style={{ color: "#3182ce" }}><MdPictureAsPdf size={18} /></a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

const thStyle = {
  padding: "14px 20px",
  fontSize: "11px",
  fontWeight: 700,
  color: "#4a5568",
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap"
};

const tdStyle = {
  padding: "14px 20px",
  whiteSpace: "nowrap",
  color: "#4a5568"
};

export default OutletOrderReport;
